import { Dispatch, SetStateAction, memo } from "react";
import { ICustomFieldDefinition } from "@Clients/CustomFieldsClient";
import { Dropdown, DropdownOption } from "@components/Dropdown/Dropdown";
import { FormInput } from "@components/FormInput/FormInput";
import { Add, Delete } from "@mui/icons-material";
import { Box, Button, IconButton, Typography } from "@mui/material";

type CustomFieldFormProps = {
    newField: Partial<ICustomFieldDefinition>;
    setNewField: Dispatch<SetStateAction<Partial<ICustomFieldDefinition>>>;
    fieldTypeOptions: DropdownOption[];
    isSaving: boolean;
    handleAddField: () => void;
    handleAddOption: () => void;
    handleRemoveOption: (index: number) => void;
    handleUpdateOption: (index: number, value: string) => void;
    duplicateOptionValues: Set<string>;
    normalizeOptionValue: (value: string) => string;
}

export const CustomFieldForm = memo(({
    newField,
    setNewField,
    fieldTypeOptions,
    isSaving,
    handleAddField,
    handleAddOption,
    handleRemoveOption,
    handleUpdateOption,
    duplicateOptionValues,
    normalizeOptionValue,
}: CustomFieldFormProps) => {
    return <Box
        sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            flex: 1,
            backgroundColor: "background.paper",
        }}
    >
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Add New Custom Field
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                }}
            >
                <Box>
                    <FormInput
                        label="Field Label"
                        value={newField.label || ""}
                        onChange={(e) =>
                            setNewField((prev) => ({ ...prev, label: e.target.value }))
                        }
                        placeholder="e.g., Job Number"
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                    >
                        Display name shown in forms
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Field Type
                    </Typography>
                    <Dropdown
                        options={fieldTypeOptions}
                        value={newField.type || "text"}
                        onChange={(value) =>
                            setNewField((prev) => ({
                                ...prev,
                                type: value as ICustomFieldDefinition["type"],
                                field_options:
                                    value === "select" ? prev.field_options || [] : [],
                            }))
                        }
                        placeholder="Select type"
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", pt: 2 }}>
                    <input
                        type="checkbox"
                        checked={newField.required || false}
                        onChange={(e) =>
                            setNewField((prev) => ({ ...prev, required: e.target.checked }))
                        }
                        style={{ marginRight: 8 }}
                    />
                    <Typography variant="body2">Required field</Typography>
                </Box>
            </Box>
            {newField.type === "select" && (
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: "background.paper",
                    }}
                >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Dropdown Options
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                        Add at least one option.
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {(newField.field_options || []).map((option, index) => (
                            <Box
                                key={`option-${index}`}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <FormInput
                                    label="Option Label"
                                    value={option.label || ""}
                                    onChange={(e) =>
                                        handleUpdateOption(index, e.target.value)
                                    }
                                    placeholder="e.g., Priority"
                                    errorMessage={
                                        option.label &&
                                            duplicateOptionValues.has(
                                                normalizeOptionValue(option.label)
                                            )
                                            ? "Duplicate option"
                                            : undefined
                                    }
                                />
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveOption(index)}
                                    sx={{ mt: { xs: 0, sm: 3 } }}
                                    title="Remove option"
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={handleAddOption}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            Add Option
                        </Button>
                    </Box>
                </Box>
            )}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                }}
            >
                <FormInput
                    label="Placeholder (Optional)"
                    value={newField.placeholder || ""}
                    onChange={(e) =>
                        setNewField((prev) => ({ ...prev, placeholder: e.target.value }))
                    }
                    placeholder="Enter placeholder text"
                />
                <FormInput
                    label="Help Text (Optional)"
                    value={newField.help_text || ""}
                    onChange={(e) =>
                        setNewField((prev) => ({ ...prev, help_text: e.target.value }))
                    }
                    placeholder="Tooltip or help text"
                />
            </Box>
            <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddField}
                disabled={isSaving}
                sx={{ alignSelf: "flex-start" }}
            >
                {isSaving ? "Adding..." : "Add Field"}
            </Button>
        </Box>
    </Box>

});
